import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import { toastSuccess, toastError, errorMessage } from '../../../lib/toast';
import ProfileCard from '../ui/ProfileCard';
import CardState from '../ui/CardState';

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = 'application/pdf,image/jpeg,image/png';
const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

// Server {error:code} -> human copy. Shared by every failure path here.
const ERRORS = {
  file_too_large: 'File is over 5MB.',
  bad_file_type: 'Upload a PDF, JPG or PNG.',
  already_uploaded: 'Already on file — contact HR to replace it.',
  no_hr_record: 'No HR record linked to your account yet.',
  odoo_unavailable: "Couldn't reach the HR system. Try again shortly.",
  odoo_access_denied: "Couldn't reach the HR system. Try again shortly.",
  rate_limited: 'Too many uploads just now. Wait a minute and try again.',
};

// Copy for the empty state, keyed by why `available` is false. A missing/
// unrecognized reason falls back to the no_hr_record copy.
const UNAVAILABLE_REASONS = {
  no_hr_record: 'No HR record linked yet. Your documents appear once your Odoo record syncs.',
  not_synced: "Your document status hasn't synced yet. Check back tomorrow.",
};

function unavailableMessage(reason) {
  return UNAVAILABLE_REASONS[reason] || UNAVAILABLE_REASONS.no_hr_record;
}

// Filed (uploaded or hardcopy) -- a plain, closed-case checkmark.
function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

// Not yet on file -- an outline document, echoing the "missing paperwork" idea.
function DocumentOutlineIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

function DocumentRow({ doc, onUpload, busy, error }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const submitFile = (file) => {
    if (file) onUpload(doc, file);
  };

  const pick = (e) => {
    const file = e.target.files && e.target.files[0];
    // Reset so choosing the same file twice still fires a change event.
    e.target.value = '';
    submitFile(file);
  };

  // Drag-and-drop is a pure enhancement over the click-to-pick control below --
  // it reuses the same submitFile/upload path, so validation and error
  // handling are identical either way.
  const onDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };
  const onDragLeave = () => setDragOver(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    submitFile(file);
  };

  const badgeKind = doc.is_uploaded ? (doc.has_file ? 'uploaded' : 'hardcopy') : null;

  return (
    <li className={`profile-doc ${doc.is_uploaded ? 'is-done' : 'is-pending'}`}>
      <span className={`profile-doc__icon profile-doc__icon--${doc.is_uploaded ? 'done' : 'pending'}`} aria-hidden="true">
        {doc.is_uploaded ? <CheckIcon /> : <DocumentOutlineIcon />}
      </span>
      <span className="profile-doc__labels">
        <span className="profile-doc__label">{doc.label}</span>
        {doc.label_ar && (
          <span className="profile-doc__label-ar" lang="ar" dir="rtl">{doc.label_ar}</span>
        )}
        {error && <span className="profile-doc__error" role="alert">{error}</span>}
      </span>
      {doc.is_uploaded ? (
        <span className={`profile-doc__badge profile-doc__badge--${badgeKind}`}>
          {doc.has_file ? 'Uploaded' : 'Hardcopy submitted'}
        </span>
      ) : (
        <span
          className={`profile-doc__action ${dragOver ? 'is-dragover' : ''}`}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            onChange={pick}
            hidden
            aria-label={`Upload ${doc.label}`}
          />
          <button
            type="button"
            className="btn btn-secondary btn-small"
            disabled={busy}
            aria-busy={busy}
            onClick={() => inputRef.current && inputRef.current.click()}
          >
            {busy ? 'Uploading…' : (error ? 'Retry' : 'Upload')}
          </button>
        </span>
      )}
    </li>
  );
}

export default function DocumentsTab({ onUploaded }) {
  const [state, setState] = useState('loading'); // loading | ok | error
  const [data, setData] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [errors, setErrors] = useState({});

  const load = useCallback(async () => {
    setState((cur) => (cur === 'error' ? 'loading' : cur));
    try {
      const r = await axios.get(`${API_BASE_URL}/api/profile/documents`, { withCredentials: true });
      setData(r.data);
      setState('ok');
    } catch {
      setState('error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (doc, file) => {
    // Pre-submit validation stays INLINE (project convention: toasts are for
    // submit outcomes, not for field validation the user can fix in place).
    if (file.size > MAX_BYTES) {
      setErrors((e) => ({ ...e, [doc.key]: ERRORS.file_too_large }));
      return;
    }
    // This check is a convenience, not the control: it trusts the browser's
    // declared MIME type, which some OS/browser combinations leave empty for
    // a perfectly valid file (notably one with no extension). An empty type
    // is let through rather than blocked -- the server sniffs magic bytes and
    // is the actual authority on whether the file is acceptable.
    if (file.type && !ACCEPTED_TYPES.includes(file.type)) {
      setErrors((e) => ({ ...e, [doc.key]: ERRORS.bad_file_type }));
      return;
    }

    setErrors((e) => ({ ...e, [doc.key]: null }));
    setBusyKey(doc.key);
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${API_BASE_URL}/api/profile/documents/${doc.key}`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toastSuccess(`${doc.label} uploaded.`);
      if (onUploaded) onUploaded();
      await load();
    } catch (err) {
      const msg = errorMessage(err, "Couldn't upload that document. Try again.", ERRORS);
      toastError(msg);
      setErrors((e) => ({ ...e, [doc.key]: msg }));
    } finally {
      setBusyKey(null);
    }
  };

  if (state === 'loading') {
    return (
      <div className="profile-card-grid">
        <ProfileCard title="Employment documents">
          <CardState kind="loading" label="Loading documents" lines={4} />
        </ProfileCard>
      </div>
    );
  }
  if (state === 'error') {
    return (
      <div className="profile-card-grid">
        <ProfileCard title="Employment documents">
          <CardState kind="error" thing="your documents" retry />
          <button type="button" className="btn btn-secondary btn-small pers-retry" onClick={load}>Try again</button>
        </ProfileCard>
      </div>
    );
  }

  const docs = data.documents || [];
  const done = docs.filter((d) => d.is_uploaded).length;

  return (
    <div className="profile-card-grid">
      <section className="profile-info-card">
        <header className="profile-info-card__head">
          <h2 className="profile-info-card__title">Employment documents</h2>
        </header>
        <p className="profile-doc__intro">
          Submitted to HR through your Odoo employee record. Once a document is on file
          it can't be replaced here — contact HR.
        </p>

        {!data.available ? (
          <div className="profile-doc-empty">
            <span className="profile-doc-empty__icon" aria-hidden="true">
              <DocumentOutlineIcon />
            </span>
            <p className="profile-doc-empty__text">{unavailableMessage(data.reason)}</p>
          </div>
        ) : (
          <>
            <div className="profile-doc-summary">
              <div className="profile-doc-summary__figure">
                <span className="profile-doc-summary__n">{done}<span className="profile-doc-summary__of">/{docs.length}</span></span>
                <span className="profile-doc-summary__l">Documents on file</span>
              </div>
              <div
                className="profile-doc__progress"
                role="progressbar"
                aria-valuenow={done}
                aria-valuemin={0}
                aria-valuemax={docs.length}
                aria-valuetext={`${done} of ${docs.length} submitted`}
                aria-label="Documents submitted"
              >
                <span style={{ width: `${docs.length ? (done / docs.length) * 100 : 0}%` }} />
              </div>
            </div>
            <ul className="profile-docs">
              {docs.map((d) => (
                <DocumentRow
                  key={d.key}
                  doc={d}
                  onUpload={upload}
                  busy={busyKey === d.key}
                  error={errors[d.key]}
                />
              ))}
            </ul>
          </>
        )}
      </section>
    </div>
  );
}
